import dayjs, { Dayjs } from 'dayjs';
import {
  AttendeeInfoFormType,
  AttendeeRequiredField,
} from '../../types/entities/event.entity';
import {
  PhoneNumberPartsSchema,
  isValidInternationalPhoneNumberParts,
  PHONE_INVALID_MESSAGE_KEY,
} from '../shared/phoneNumber.schema';
import { z } from 'zod';

const stringValidation = z.string().min(1);

export const AttendeesInfoFormValidatorSchema = z.object({
  language: stringValidation,
  email: stringValidation,
  state: stringValidation,
  country: stringValidation,
  countryCode: stringValidation,

  address: z.object({
    state: stringValidation,
    zipCode: stringValidation,
    city: stringValidation,
    line1: stringValidation,
    line2: z.string().optional().nullable(),
    line3: z.string().optional().nullable(),
    countryCode: z.string().optional().nullable(),
    country: z.string().optional().nullable(),
  }),
  company: stringValidation,
  title: stringValidation,
  lastName: stringValidation,
  firstName: stringValidation,
  birthDate: z.instanceof(dayjs as unknown as typeof Dayjs, {
    message: 'Birth date was not configured',
  }),
  idNumber: stringValidation,
  citizenshipCountryCode: stringValidation,
  phoneNumber: PhoneNumberPartsSchema,
  ticketUuid: z.string().optional().nullable(),
  ticketDescription: z.string().optional().nullable(),
  deepLinkUrl: z.string().optional().nullable(),
});

export const attendeeFields: {
  name: AttendeeRequiredField;
  required: boolean;
}[] = [
  { name: 'language', required: true },
  { name: 'email', required: true },
  // { name: 'city', required: true },
  // { name: 'state', required: true },
  { name: 'address', required: true },
  { name: 'company', required: true },
  { name: 'country', required: true },
  { name: 'title', required: true },
  { name: 'lastName', required: true },
  { name: 'firstName', required: true },
  { name: 'birthDate', required: true },
  { name: 'idNumber', required: true },
  { name: 'citizenshipCountryCode', required: true },
  { name: 'phoneNumber', required: true },
];

/** The attendee fields the form schema actually declares a validator for. */
type AttendeesInfoFormField = keyof typeof AttendeesInfoFormValidatorSchema.shape;

const isAttendeesInfoFormField = (
  field: string
): field is AttendeesInfoFormField =>
  field in AttendeesInfoFormValidatorSchema.shape;

/**
 * Builds the attendee schema for the fields a waiting list actually requires.
 * Which keys are present is only known at runtime, so the declared type is the
 * full {@link AttendeeInfoFormType} — every field optional — and the runtime
 * schema enforces the configured subset.
 */
export function generateConditionalAttendeesInfoFormValidatorSchema(
  fields?: AttendeeRequiredField[]
): z.ZodType<AttendeeInfoFormType, AttendeeInfoFormType> {
  let schema = z.object({});
  if (!fields) {
    return schema as unknown as z.ZodType<
      AttendeeInfoFormType,
      AttendeeInfoFormType
    >;
  }
  // *** Configuring address schema depends on fields
  let addressSchema: {
    [key: string]: z.ZodString | z.ZodNullable<z.ZodOptional<z.ZodString>>;
  } = {
    city: AttendeesInfoFormValidatorSchema.shape['address'].shape.city,
    zipCode: AttendeesInfoFormValidatorSchema.shape['address'].shape.zipCode,
    state: AttendeesInfoFormValidatorSchema.shape['address'].shape.state,
  };

  if (fields.includes('address')) {
    addressSchema = {
      ...addressSchema,
      line1: AttendeesInfoFormValidatorSchema.shape['address'].shape.line1,
      line2: AttendeesInfoFormValidatorSchema.shape['address'].shape.line2,
      line3: AttendeesInfoFormValidatorSchema.shape['address'].shape.line3,
    };
  }

  const addressFields: Array<AttendeeRequiredField> = [
    'city',
    'state',
    'zipCode',
  ];

  addressFields.forEach((field) => {
    if (!fields.includes(field)) {
      delete addressSchema[field as keyof typeof addressSchema];
    }
  });

  // ***

  const filtered = fields.filter((field) => !addressFields.includes(field));
  if (Object.keys(addressSchema).length > 0) {
    filtered.push('address');
  }

  filtered.forEach((field) => {
    const isRequired = !!attendeeFields.find(
      (attendeeField) => attendeeField.name === field
    )?.required;

    if (field === 'address') {
      const address = z.object({ ...addressSchema });
      schema = z.object({
        ...schema.shape,
        address: isRequired ? address : address.optional(),
      });
      return;
    }

    // The phone's local-number part is validated through the whole phone field.
    const shapeKey = field === 'phoneNumber.localNumber' ? 'phoneNumber' : field;

    if (!isAttendeesInfoFormField(shapeKey)) {
      return;
    }

    const fieldSchema = AttendeesInfoFormValidatorSchema.shape[shapeKey];
    schema = z.object({
      ...schema.shape,
      [shapeKey]: isRequired ? fieldSchema : fieldSchema.optional(),
    });
  });

  if (
    fields.includes('phoneNumber') ||
    fields.includes('phoneNumber.localNumber')
  ) {
    return (schema as unknown as z.ZodType<
      AttendeeInfoFormType,
      AttendeeInfoFormType
    >).superRefine((values, ctx) => {
      const phone = (values as any).phoneNumber;
      if (!phone) return;
      if (
        !isValidInternationalPhoneNumberParts(
          phone.countryCallingCode,
          phone.localNumber
        )
      ) {
        ctx.addIssue({
          code: 'custom',
          message: PHONE_INVALID_MESSAGE_KEY,
          path: ['phoneNumber', 'localNumber'],
        });
      }
    });
  }

  return schema as unknown as z.ZodType<
    AttendeeInfoFormType,
    AttendeeInfoFormType
  >;
}

export const AttendeesInfoValidatorSchema = z.object({
  attendees: z.array(AttendeesInfoFormValidatorSchema),
});

export type AttendeesInfoValidator = z.infer<
  typeof AttendeesInfoValidatorSchema
>;

export type AttendeesInfoFormValidator = z.infer<
  typeof AttendeesInfoFormValidatorSchema
>;
